import React, { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../config/api";
import secureStorage from "../../utils/secureStorage";
import { Modal, ListLoader } from "../../stock/StockUI";

/**
 * Historique des versions d'une recette (Étape 5).
 * Lecture seule — chaque version passée est un snapshot complet pris juste
 * avant qu'une édition ne la remplace (voir RecipeStateProcessor côté backend).
 */
const RecipeVersionHistoryModal = ({ recipeId, recipeName, isDarkMode, onClose }) => {
    const [expandedVersion, setExpandedVersion] = useState(null);

    const { data, isLoading, error } = useQuery({
        queryKey: ['recipe-versions', recipeId],
        queryFn: async () => {
            const accountId = await secureStorage.getItem("selectedAccount");
            const response = await api.get(`/recipes/${recipeId}/versions`, {
                headers: { 'X-account-id': accountId.id }
            });
            return response.data;
        },
        enabled: !!recipeId
    });

    const allVersions = data ? [data.current_version, ...data.previous_versions] : [];

    // Flat row split by a hairline, expandable to the full snapshot
    const VersionRow = ({ entry, isCurrent }) => {
        const isExpanded = expandedVersion === entry.version;
        return (
            <div>
                <button
                    type="button"
                    onClick={() => setExpandedVersion(isExpanded ? null : entry.version)}
                    className="w-full flex items-center justify-between gap-3 px-6 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                    <div className="flex items-center gap-2 min-w-0">
                        {isExpanded
                            ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full flex-shrink-0 ${isCurrent ? 'bg-primary/10 text-primary-dark dark:text-primary' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`}>
                            v{entry.version}{isCurrent ? ' (actuelle)' : ''}
                        </span>
                        <span className="text-sm text-gray-500 dark:text-gray-400 truncate">
                            {new Date(entry.created_at).toLocaleString('fr-FR')}
                        </span>
                    </div>
                    <span className="text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-100 flex-shrink-0">
                        {Number(entry.data.cost).toFixed(2)} DH HT
                    </span>
                </button>
                {isExpanded && (
                    <div className="px-6 pb-4 pl-12 space-y-3 text-sm">
                        <p className="text-gray-600 dark:text-gray-400">
                            Rendement : {entry.data.yield_quantity} {entry.data.yield_unit || ''} · Coût TTC : {Number(entry.data.cost_ttc).toFixed(2)} DH
                        </p>
                        {entry.data.ingredients.length > 0 && (
                            <div>
                                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Ingrédients</p>
                                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300">
                                    {entry.data.ingredients.map((ing, i) => (
                                        <li key={i}>{ing.quantity} {ing.consumption_unit || ''} {ing.product_name || 'Produit supprimé'}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {entry.data.sub_recipe_usages.length > 0 && (
                            <div>
                                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Sous-recettes</p>
                                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300">
                                    {entry.data.sub_recipe_usages.map((u, i) => (
                                        <li key={i}>{u.quantity} {u.unit || ''} {u.sub_recipe_name || 'Recette supprimée'}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <Modal
            title="Historique des versions"
            subtitle={recipeName}
            onClose={onClose}
            size="lg"
            bodyClass=""
            footer={
                <button type="button" onClick={onClose}
                        className="w-full py-4 border-t border-gray-200 dark:border-gray-700 font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                    Fermer
                </button>
            }
        >
            {isLoading && <ListLoader label="Chargement..." />}
            {error && <p className="px-6 py-8 text-center text-sm text-red-500">Erreur lors du chargement de l'historique</p>}
            {data && (
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {allVersions.map((entry, i) => (
                        <VersionRow key={entry.version} entry={entry} isCurrent={i === 0} />
                    ))}
                </div>
            )}
        </Modal>
    );
};

export default RecipeVersionHistoryModal;
